import { useEffect, useState, useCallback, useRef } from 'react';
import { databases } from '@/lib/appwrite';
import { Query } from 'appwrite';
import type { NoteType } from '@/lib/collections/WorkSessionNotes/type';
import { realtimeManager } from '@/lib/manager realtime/realtimeManager';

export function useRealtimeNotes(
  databaseId: string,
  collectionId: string
): [NoteType[], boolean] {
  const [notes, setNotes] = useState<NoteType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const subscriptionIdRef = useRef<string | null>(null);
  const isUnmountedRef = useRef(false);

  // Función para cargar notas iniciales
  const loadInitialNotes = useCallback(async () => {
    if (isUnmountedRef.current) return;
    
    setIsLoading(true);
    try {
      const resp = await databases.listDocuments(databaseId, collectionId, [
        Query.equal('isRead', false),
        Query.orderDesc('createdAt'),
        Query.limit(50) // Limitar para mejor performance
      ]);
      
      if (!isUnmountedRef.current) {
        setNotes(resp.documents as unknown as NoteType[]);
      }
    } catch (error) {
      // console.error('Error cargando notas:', error);
      if (!isUnmountedRef.current) {
        setNotes([]);
      }
    } finally {
      if (!isUnmountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [databaseId, collectionId]);

  // Callback optimizado para eventos de realtime
  const handleRealtimeEvent = useCallback((response: any) => {
    if (isUnmountedRef.current) return;
    
    try {
      // console.log('📝 Evento realtime de notas:', response.events);

      // Si es creación de nueva nota
      if (response.events.some((e: string) => e.endsWith('.create'))) {
        const newNote = response.payload as NoteType;
        // Solo agregar si no está leída
        if (!newNote.isRead) {
          setNotes(prev => {
            // Evitar duplicados
            if (prev.some(n => n.$id === newNote.$id)) {
              return prev;
            }
            return [newNote, ...prev];
          });
        }
      }

      // Si es actualización (ej: marcar como leído)
      if (response.events.some((e: string) => e.endsWith('.update'))) {
        const updatedNote = response.payload as NoteType;
        
        setNotes(prev => {
          // Si la nota fue marcada como leída, removerla
          if (updatedNote.isRead) {
            return prev.filter(n => n.$id !== updatedNote.$id);
          }
          // Si no está leída, actualizar o agregar
          const existingIndex = prev.findIndex(n => n.$id === updatedNote.$id);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = updatedNote;
            return updated;
          } else {
            return [updatedNote, ...prev];
          }
        });
      }

      // Si es eliminación
      if (response.events.some((e: string) => e.endsWith('.delete'))) {
        const deletedNote = response.payload as NoteType;
        setNotes(prev => prev.filter(n => n.$id !== deletedNote.$id));
      }

    } catch (error) {
      // console.warn('Error procesando evento realtime de notas:', error);
    }
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    
    // Cargar notas iniciales
    loadInitialNotes();

    // Suscribirse al realtime
    const subscribeToRealtime = async () => {
      try {
        const channel = `databases.${databaseId}.collections.${collectionId}.documents`;
        const subscriptionId = await realtimeManager.subscribe([channel], handleRealtimeEvent);
        subscriptionIdRef.current = subscriptionId;
        // console.log('✅ Suscrito a notas realtime:', channel);
      } catch (error) {
        // console.warn('❌ Error suscribiendo a notas realtime:', error);
      }
    };

    subscribeToRealtime();

    return () => {
      isUnmountedRef.current = true;
      
      // Desuscribirse
      if (subscriptionIdRef.current) {
        realtimeManager.unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [databaseId, collectionId, handleRealtimeEvent, loadInitialNotes]);

  return [notes, isLoading];
}